import { useFormikContext } from 'formik'

import { Alert, Form } from '@/ui'
import { getProjectedW2Income } from './helpers'
import { useReselector } from '@/utils/sharedHooks'
import { formatCurrency } from '@/utils/currencyHelpers'
import { W2IncomeWarning } from './W2IncomeWarning'
import {
  PaystubVsEstimateAccordion,
  PaystubAccordionContent,
} from './PaystubVsEstimateAccordion'
import { QuarterlyTaxEstimateDetail } from '@/features/admin/quarterly-tax-estimate-details/quarterlyTaxEstimateDetails.slice'
import { selectActiveQuarterlyTaxEstimateDetails } from '@/features/admin/quarterly-tax-estimate-details/quarterlyTaxEstimateDetails.selector'
// TODO: Migrate to V2
import {
  FormikLabelError,
  FormikRadioToggleButton,
  getFieldName,
  makeNumberSchema,
  makeReqBoolSchema,
} from '@/componentsV1/BaseComponents'

import { LabelDescription } from '@/componentsV1/BaseComponents/Input'
interface W2Values {
  moneyOutsideOfPractice: boolean | undefined
  wIncome: number | null | undefined
  enterPaystubW2: boolean | null
  estimatedAnnualW2Income?: number | null
}

const w2IncomeCopy = (activeQuarterDetails?: QuarterlyTaxEstimateDetail) => {
  switch (activeQuarterDetails?.taxQuarter) {
    case '1':
      return "Enter income earned from your additional W-2 job(s) for pay periods starting January 1st through February 28th. You'll find this on the first paycheck you received in March."
    case '2':
      return "Enter income earned from your additional W-2 job(s) for pay periods starting January 1st through April 30th. You'll find this on the first paycheck you received in May."
    case '3':
      return "Enter income earned from your additional W-2 job(s) for pay periods starting January 1st through July 31st. You'll find this on the first paycheck you received in August."
    case '4':
      return "Enter income earned from your additional W-2 job(s) for pay periods starting January 1st through November 30st. You'll find this on the first paycheck you received in December."
    // should never happen, but fall back to generic copy
    default:
      return 'Please include income only from your additional W-2 job(s). You can find this on your most recent paystub(s).'
  }
}

export const W2Job = ({ isScorp }: { isScorp: boolean }) => {
  const activeQuarterDetails = useReselector(
    selectActiveQuarterlyTaxEstimateDetails
  )

  const { values, touched } = useFormikContext<W2Values>()

  const projectedOtherW2Income = getProjectedW2Income({
    ytdIncomeInDollars: values.wIncome,
    quarterDetails: activeQuarterDetails,
  })

  const w2IncomeSubcopy = w2IncomeCopy(activeQuarterDetails)

  return (
    <div className="w-full space-y-6">
      <div className="max-w-2xl mx-auto">
        <FormikLabelError
          name={getFieldName<W2Values>('moneyOutsideOfPractice')}
          label={`Do you have ${isScorp ? 'additional' : 'a'} W-2 job${isScorp ? 's' : ''}?`}
          description={
            <>
              We’re referring to W-2 jobs <b>outside</b> of your private
              practice, e.g. working for a group practice, hospital, government
              agency, etc.
            </>
          }
          schema={makeReqBoolSchema()}
        />
      </div>

      <div className="max-w-2xl mx-auto flex flex-col sm:flex-row justify-center gap-4">
        <div className="sm:w-1/2 w-full">
          <FormikRadioToggleButton
            name={getFieldName<W2Values>('moneyOutsideOfPractice')}
            value
            fullWidth
          >
            Yes
          </FormikRadioToggleButton>
        </div>
        <div className="sm:w-1/2 w-full">
          <FormikRadioToggleButton
            name={getFieldName<W2Values>('moneyOutsideOfPractice')}
            value={false}
            fullWidth
          >
            No
          </FormikRadioToggleButton>
        </div>
      </div>

      {values.moneyOutsideOfPractice && (
        <div className="space-y-6">
          <div className="max-w-2xl mx-auto space-y-4">
            <FormikLabelError
              name={getFieldName<typeof values>('enterPaystubW2')}
              label="How would you like to enter income?"
              description="Click on “How should I choose?” below if you need help deciding how to enter income."
              schema={makeReqBoolSchema()}
            />
            <div className="flex flex-col sm:flex-row gap-4">
              <FormikRadioToggleButton
                name={getFieldName<typeof values>('enterPaystubW2')}
                value
                fullWidth
              >
                Year-to-date from paystub
              </FormikRadioToggleButton>
              <FormikRadioToggleButton
                name={getFieldName<typeof values>('enterPaystubW2')}
                value={false}
                fullWidth
              >
                Estimated income
              </FormikRadioToggleButton>
            </div>
          </div>

          {/* YTD income entry */}
          {values.enterPaystubW2 && (
            <div className="max-w-2xl mx-auto space-y-4">
              <LabelDescription
                required
                label={`Year-to-Date gross income from${isScorp ? ' additional ' : ' '}W-2 job(s)`}
                description={w2IncomeSubcopy}
              />
              <div className="flex flex-col lg:flex-row gap-4 w-full items-center">
                <div className="w-1/2">
                  <Form.Input
                    name={getFieldName<typeof values>('wIncome')}
                    required
                    componentType="currency"
                    schema={makeNumberSchema({
                      allowedDecimals: 2,
                      greaterThanZero: true,
                    })}
                    placeholder="$"
                  />
                </div>
                <div className="w-1/2">
                  {projectedOtherW2Income && (
                    <Alert
                      className="w-full p-4"
                      title={`${formatCurrency(projectedOtherW2Income, {
                        hideDecimalsIfZero: true,
                      })} projected annually`}
                    />
                  )}
                </div>
              </div>

              <W2IncomeWarning
                inputTouched={touched.wIncome}
                w2Income={values.wIncome}
              />
            </div>
          )}

          {/* Estimated income entry */}
          {values.enterPaystubW2 === false && (
            <div className="max-w-2xl mx-auto">
              <Form.Input
                name={getFieldName<typeof values>('estimatedAnnualW2Income')}
                required
                componentType="currency"
                schema={makeNumberSchema({
                  allowedDecimals: 2,
                  greaterThanZero: true,
                })}
                placeholder="$"
                label={
                  isScorp
                    ? `${activeQuarterDetails?.taxYear} Annual gross income for all other W-2 job(s)`
                    : `${activeQuarterDetails?.taxYear} Annual gross income for your W-2 job`
                }
                description={
                  isScorp
                    ? 'Please include income only from your additional W-2 job(s).'
                    : 'Please include income only from your W-2 job'
                }
              />
            </div>
          )}

          {/* Accordion */}
          <div className="max-w-3xl mx-auto">
            <PaystubVsEstimateAccordion
              content={PaystubAccordionContent.sprop}
            />
          </div>
        </div>
      )}
    </div>
  )
}
